import { Flex } from '@chakra-ui/react';
import SelectableOption from 'components/common/Selectable/SelectableOption';
import InfiniteScroll, { InfiniteScrollProps } from 'components/common/InfiniteScroll/InfiniteScroll';
import { specialFont } from 'constants/theme';
import DropDown, { DropDownProps } from './DropDown';
import { IParameter } from '../../../../server/types';

interface ISelectableOption {
  value: string | boolean;
  displayName: string;
}

type SelectableProps = {
  scrollProps?: InfiniteScrollProps;
  dropDown?: boolean;
  onChange(...args: unknown[]): void;
  parameter?: IParameter;
  options: ISelectableOption[] | string[] | null;
  dropDownProps?: Partial<DropDownProps>;
  value?: string | boolean;
};

const Selectable = ({ scrollProps, dropDown, onChange, parameter, options, value, dropDownProps }: SelectableProps) => {
  const url = window.location.href;

  const sortParamValues = (obj: object) => {
    const unsortedArr = Object.entries(obj);

    const tempArr = unsortedArr.map((val) => {
      const param = val[1].displayName;
      return [param, val[1]];
    });

    const collator = new Intl.Collator(undefined, {
      numeric: true,
      sensitivity: 'base',
    });

    const result = Object.fromEntries((tempArr as any).sort(collator.compare));

    return result;
  };

  const renderDisplayName = (values: ISelectableOption[]) => {
    const found = Object.values(values).find((val) => {
      const isTruthy = value === true || (value as string).toLowerCase() === 'true';
      const isMatch =
        `${value}`.toLowerCase() === val.value.toString().toLocaleLowerCase() ||
        `${value}`.toLowerCase() === val.displayName.toString().toLocaleLowerCase();
      const isBoolMatch = isTruthy === val.value;

      return isMatch || isBoolMatch;
    });

    if (found === undefined) return undefined;

    return found?.displayName;
  };

  const renderDropdown = (values: string[], key: string) => (
    <DropDown
      {...dropDownProps}
      name={parameter?.name}
      selectValue={key}
      filterData={values}
      setFilteredData={(_: string, filteredValue: string) => onChange(filteredValue)}
      value={renderDisplayName((options as ISelectableOption[]) ?? [])}
    />
  );

  const handleGetDropdownKey = (dropDownValues: string[], boolDropDownValues: ISelectableOption[]) => {
    const dropdownValueLower = dropDownValues[0].toLowerCase();
    const dropdownValueTruthy = (value as string).toLowerCase() === 'true';
    const dropdownIndex = +dropdownValueTruthy;
    const boolDropdownIndex = +!dropdownValueTruthy;
    return dropdownValueLower === 'true' || dropdownValueLower === 'false'
      ? dropDownValues[dropdownIndex]
      : boolDropDownValues[boolDropdownIndex].displayName;
  };

  const renderOptions = (values: ISelectableOption[]) => {
    const productValues = url.includes('newproduct') ? values : sortParamValues(values);

    if (!productValues) return null;

    const dropDownValues = Object.values(productValues).map((item) => item.displayName);
    const boolDropDownValues = Object.values(values);

    if (dropDown) {
      const isDefaultValueBool =
        (value as string).toLowerCase() === 'true' || (value as string).toLowerCase() === 'false';
      const dropdownKey = isDefaultValueBool ? handleGetDropdownKey(dropDownValues, boolDropDownValues) : value;
      return renderDropdown(dropDownValues, dropdownKey as string);
    }

    const optionsMap =
      typeof productValues === 'object' ? Object.values(productValues) : (productValues as ISelectableOption[]);
    return optionsMap.map((val) => {
      const { value: optionsMapValue, displayName } = val;
      const optionName = displayName ?? val;

      let selected = false;
      let pVal = value;
      if (typeof value === 'string' && value.toLowerCase() === 'false') {
        pVal = false;
      }

      if (typeof pVal === 'string' && pVal.toLowerCase() === 'true') {
        pVal = true;
      }

      if (optionsMapValue === pVal) {
        selected = true;
      }
      return (
        <SelectableOption
          key={optionName}
          optionName={optionName}
          optionValue={optionsMapValue}
          selected={selected}
          selectOption={onChange}
        />
      );
    });
  };

  const renderNote = parameter?.note ? (
    <Flex
      sx={{
        py: 1,
        flex: 1,
        color: 'base2FontColour.500',
        fontSize: 'sm',
        fontWeight: 'light',
      }}
    >
      {parameter.note}
    </Flex>
  ) : null;

  const valid = parameter?.valueConditions ? parameter.valid : true;

  const renderOptionsContainer =
    !dropDown && scrollProps ? (
      <InfiniteScroll {...scrollProps}>{renderOptions((options as ISelectableOption[]) ?? [])}</InfiniteScroll>
    ) : (
      renderOptions((options as ISelectableOption[]) ?? [])
    );

  const renderError = !valid ? (
    <Flex
      sx={{
        fontFamily: specialFont,
        fontSize: 'sm',
        fontWeight: 'light',
        pb: 1,
        color: 'red',
      }}
    >
      Please select a valid parameter.
    </Flex>
  ) : null;

  return (
    <>
      {renderNote}
      {renderError}
      <Flex
        sx={{
          flexDir: 'column',
          flex: 1,
          bg: 'white',
          border: dropDown ? '1px solid' : 'none',
          borderColor: 'base2Colour.400',
          borderRadius: 4,
          mt: dropDown ? 2 : 1,
        }}
      >
        {renderOptionsContainer}
      </Flex>
    </>
  );
};

export default Selectable;
